import { InspoStory } from '../../../services';

export type StoryStep = { title: string; body: string; cta?: { label: string; href: string } };

/** A hero-design share below this is too spread out to call "hero designs". */
const HERO_SHARE_MIN = 15;

// #region firstName
export const firstName = (story: InspoStory): string | null => story.founder_name?.trim().split(/\s+/)[0] ?? null;

// #region namedAfterFounder
/** True when the brand carries the founder's name, e.g. "Samantha Santana Wallpaper". */
export const namedAfterFounder = (story: InspoStory): boolean =>
    !!story.founder_name && story.name.toLowerCase().includes(story.founder_name.toLowerCase());

// #region storefrontHost
export const storefrontHost = (story: InspoStory): string | null => story.storefront_url?.replace(/^https:\/\//, '') ?? null;

// #region narrativeOf
/** The story in plain words: the admin's text when written, else facts read straight from the data. */
export const narrativeOf = (story: InspoStory): string => {
    if (story.story) return story.story;
    const who = story.founder_name ?? story.name;
    const style = story.motif ? `${story.motif} ` : '';
    const where = storefrontHost(story) ? `on ${storefrontHost(story)}` : 'on the brand’s own Shopify store';
    const as = namedAfterFounder(story) ? '' : ` as ${story.name}`;
    return `${who} sells ${style}wallpaper${as} ${where}, and Wallmates prints and ships every order. `
        + `Today the brand brings in ${story.figures.monthly_now} a month in customer sales, from ${story.figures.customers} customers so far.`;
};

// #region stepsOf
/** What they did, in three steps read from their numbers, each pointing at the Wallmates feature behind it. */
export const stepsOf = (story: InspoStory): StoryStep[] => {
    const hero = story.hero_designs;
    const first: StoryStep = hero && hero.share_pct >= HERO_SHARE_MIN
        ? {
            title: 'Found their hero designs',
            body: `“${hero.titles[0]}” leads the way. Their top 3 designs make up ${hero.share_pct}% of recent orders.`,
            cta: { label: 'Create a design', href: '/design-creation' }
        }
        : {
            title: 'Built a deep catalog',
            body: `${story.figures.designs} designs, so every customer finds their style. Sales are spread across the whole range.`,
            cta: { label: 'Create a design', href: '/design-creation' }
        };
    return [
        first,
        {
            title: 'Sold on their own store',
            body: `Customers buy ${storefrontHost(story) ? `at ${storefrontHost(story)}` : 'on their own Shopify store'}. Wallmates prints, packs and ships every order.`,
            cta: { label: 'Connect Shopify', href: '/settings?tab=channels' }
        },
        {
            title: 'Kept customers coming back',
            body: `${story.figures.repeat_rate}% of their customers have ordered more than once.`
        }
    ];
};
